import React, { useEffect, useMemo, useState, useRef} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Typography,
  IconButton,
  Tab,
  Tabs,
  CardMedia,
  useTheme,
  ThemeProvider,
  createTheme,
  styled,
  Skeleton,
  Alert,
  CircularProgress,
  useMediaQuery,
  Button,
  Chip
} from '@mui/material';

import {
  Close as CloseIcon,
  Info,
  Directions,
  ExpandLess,
  ExpandMore,
  DirectionsCar,
  ArrowUpward,
  Star,
  Speed,
  LocalGasStation,
  EventSeat,
  ColorLens,
  ArrowBackIos,
  ArrowForwardIos,
} from '@mui/icons-material';

import CustomScrollbar from '../../CustomScrollbar';
import ImageGallery from './ImageGallery';
import Accessories from './Accessories';
import { StyledComponentsThemWrapper } from '../../StyledComponentsThemWrapper';
import { trackChatEvent } from '../../../utils/analytics';
import ProgressiveImage from './ProgressiveImage';
import { useWebSocketContext } from '../../../context/Theme';
import { createTranslator, getBrowserLanguage } from '../../../utils/i18n';

// Enhanced theme with modern styling
const customTheme = createTheme({
  typography: {
    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
          color: "#FFFFFF",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 20px rgba(37, 99, 235, 0.25)",
          }
        },
      },
    },
  },
});

const formatPrice = (price, locale = "it-IT") => {
  return parseFloat(price).toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const defaultImage = "https://www.aitomotivelab.com/images/logo/AitomotiveLab_White.svg";

// Add a fallback image constant
const FALLBACK_IMAGE = `${process.env.REACT_APP_DOMAIN}/media/company_logos/3_ms-icon-144x144.png`;

// Minimalistic header design
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: '#ffffff',
  color: '#1e293b',
  padding: theme.spacing(2.5, 3),
  position: "relative",
  borderBottom: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  letterSpacing: "0.025em",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  color: '#64748b',
  minHeight: '56px',
  padding: theme.spacing(2, 3),
  position: 'relative',
  borderRadius: '12px 12px 0 0',
  "&.Mui-selected": {
    color: '#667eea',
    fontWeight: 700,
    backgroundColor: 'rgba(102, 126, 234, 0.08)',
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: "32px",
      height: "3px",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: "3px 3px 0 0",
    }
  },
  "&:hover": {
    backgroundColor: "rgba(102, 126, 234, 0.04)",
    transform: "translateY(-1px)",
    color: '#667eea',
  }
}));

const InfoCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: "12px",
  backgroundColor: "#ffffff",
  position: "relative",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    borderColor: "rgba(0,0,0,0.12)",
  }
}));

// Enhanced TabPanel with improved animations and proper scrolling
const TabPanel = ({ children, value, index }) => (
  <motion.div
    role="tabpanel"
    initial={{ opacity: 0, x: 20 }}
    animate={{ 
      opacity: value === index ? 1 : 0,
      x: value === index ? 0 : 20,
      display: value === index ? 'flex' : 'none',
    }}
    transition={{ 
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }}
    style={{ 
      height: '100%',
      position: 'absolute',
      width: '100%',
      top: 0,
      left: 0,
      overflow: 'auto',
      display: value === index ? 'block' : 'none'
    }}
  >
    {value === index && children}
  </motion.div>
);

const GeneralInfo = ({ vehicleData, t, numberLocale }) => {
  const theme = useTheme();

  const infoItems = [
    {
      label: t("yearLabel"),
      value: vehicleData?.registrationYear,
      type: "text",
      icon: <EventSeat sx={{ color: '#64748b', fontSize: 20 }} />,
      check: true,
    },
    {
      label: t("colorLabel"),
      value: vehicleData?.colour,
      type: "text",
      icon: <ColorLens sx={{ color: '#64748b', fontSize: 20 }} />,
    },
    {
      label: t("kilometersLabel"),
      value: vehicleData?.km,
      type: "text",
      icon: <Speed sx={{ color: '#64748b', fontSize: 20 }} />,
      check: true,
    },
    {
      label: t("listPriceLabel"),
      value: vehicleData?.retailPrice || 0,
      type: "price",
      priceKind: "list",
      icon: <Star sx={{ color: '#64748b', fontSize: 20 }} />,
      check: true,
      // Hide retail price for used cars
      hide: (() => {
        const badgeLower = (vehicleData?.badge || "").toLowerCase();
        return badgeLower !== "nuovo" && 
               badgeLower !== "km zero" && 
               badgeLower !== "km0" && 
               badgeLower !== "km 0";
      })()
    },
    {
      label: t("finalPriceLabel"),
      value: vehicleData?.salePrice || 0,
      type: "price",
      priceKind: "final",
      icon: <Star sx={{ color: '#10b981', fontSize: 20 }} />,
      check: true,
      highlight: true,
    },
    {
      label: t("modelLabel"),
      value: vehicleData?.modelGroup,
      type: "text",
      icon: <DirectionsCar sx={{ color: '#64748b', fontSize: 20 }} />,
    },
    {
      label: t("fuelLabel"),
      value: vehicleData?.fuel,
      type: "text",
      icon: <LocalGasStation sx={{ color: '#64748b', fontSize: 20 }} />,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Grid container spacing={3}>
        {infoItems
          .filter(item => {
            // Skip items that should be hidden
            if (item.hide) return false;
            
            if (item.check) {
              const value = parseFloat(item.value);
              return !isNaN(value) && value > 0;
            }
            return item.value?.toString().trim() !== "" && item.value != null;
          })
          .map((item, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  delay: index * 0.1,
                  duration: 0.4,
                  ease: [0.4, 0, 0.2, 1]
                }}
              >
                <InfoCard
                  sx={{
                    background: item.highlight ? 'rgba(16,185,129,0.04)' : 'white',
                    borderColor: item.highlight ? 'rgba(16,185,129,0.2)' : 'rgba(0,0,0,0.08)'
                  }}
                >
                  <Box sx={{ 
                    display: "flex", 
                    alignItems: "flex-start", 
                    gap: 1.5,
                    mb: 1.5,
                  }}>
                    {item.icon}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ 
                          color: '#6b7280',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          mb: 0.5
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography 
                    variant="body1"
                    sx={{
                      fontWeight: item.highlight ? 700 : 600,
                      color: item.type === "price" ?
                        (item.priceKind === "list" ? '#9ca3af' :
                         item.highlight ? '#059669' : '#1e293b') : 
                        '#1e293b',
                      fontSize: item.highlight ? "1.25rem" : "1rem",
                      textDecoration: item.priceKind === "list" ? 'line-through' : 'none',
                      lineHeight: 1.3
                    }}
                  >
                    {item.type === "price" ? `${formatPrice(item.value, numberLocale)} €` : item.value}
                  </Typography>
                </InfoCard>
              </motion.div>
            </Grid>
          ))}
      </Grid>
    </motion.div>
  );
};

// Add a loading skeleton
const LoadingSkeleton = () => (
  <div className="vehicle-dialog-skeleton">
    <div className="image-skeleton animate-pulse"></div>
    <div className="content-skeleton">
      <div className="title-skeleton animate-pulse"></div>
      <div className="details-skeleton animate-pulse"></div>
    </div>
  </div>
);

// Modify the image loading logic
const handleImageError = (e) => {
  if (e && e.target) {
    e.target.src = FALLBACK_IMAGE;
    e.target.style.objectFit = 'contain';
    e.target.style.padding = '20px';
    e.target.style.backgroundColor = '#f5f5f5';
  }
};

// Main VehicleDialog component
const VehicleDialog = ({ 
  open, 
  handleClose, 
  vehicleData, 
  vehicleid, 
  isLoading = true,
  setIsLoading = () => {},
  showFullScreenGallery = false
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const loadingTimerRef = useRef(null);
  const unavailableTimerRef = useRef(null);
  const theme = useTheme();
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  const numberLocale = language === "en" ? "en-US" : language === "es" ? "es-ES" : "it-IT";
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { handleSend, isLoading: chatLoading } = useWebSocketContext() || {};

  const isNewBadge = (vehicleData?.badge || '').toLowerCase() === 'nuovo';

  const handleBookCTA = () => {
    if (!vehicleData) return;
    const message = t("bookVisitMessage", {
      title: vehicleData.title || vehicleData.modelGroup,
      vehicleId: vehicleid
    });
    trackChatEvent.bookVisit && trackChatEvent.bookVisit(vehicleid, vehicleData.title || vehicleData.modelGroup);
    if (handleSend) handleSend(message, null, null, { type: 'chat_message', sender: 'user' });
    // Close the dialog immediately so the user returns to chat
    // Use a short timeout to avoid interfering with click animations
    setTimeout(() => {
      handleClose && handleClose();
    }, 10);
  };

  // Add ref for the dialog
  const dialogRef = useRef(null);

  // Add focus management
  useEffect(() => {
    if (open) {
      // Store the currently focused element
      const previouslyFocused = document.activeElement;
      
      // Focus the dialog after it opens
      if (dialogRef.current) {
        dialogRef.current.focus();
      }

      return () => {
        // Restore focus when dialog closes
        if (previouslyFocused instanceof HTMLElement) {
          previouslyFocused.focus();
        }
      };
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      // Don't set global loading state
      // if (typeof setIsLoading === 'function') {
      //   setIsLoading(true);
      // }
      setShowUnavailable(false);
      
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
      if (unavailableTimerRef.current) clearTimeout(unavailableTimerRef.current);
      
      if (vehicleData === null) {
        unavailableTimerRef.current = setTimeout(() => {
          setShowUnavailable(true);
        }, 10000);
      }
    }

    return () => {
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
      if (unavailableTimerRef.current) clearTimeout(unavailableTimerRef.current);
    };
  }, [open, vehicleData]);

  useEffect(() => {
    if (vehicleData?.urlMainImage) {
      const baseUrl = vehicleData.urlMainImage.replace(/\/\d+\/large$/, "");
      setImages([0, 1, 2, 3, 4, 5].map((index) => `${baseUrl}/${index}/large`));
    } else {
      setImages([defaultImage]);
    }
  }, [vehicleData]);

  useEffect(() => {
    if (showFullScreenGallery && vehicleData?.urlMainImage && !fullScreenImage) {
      const baseUrl = vehicleData.urlMainImage.replace(/\/\d+\/large$/, "");
      const firstImage = `${baseUrl}/0/large`;
      setFullScreenImage(firstImage);
    }
  }, [showFullScreenGallery, vehicleData, fullScreenImage]);

  const shouldShowUnavailable = !isLoading && (
    (vehicleData === null && showUnavailable) ||
    vehicleData?.available === false
  );

  const handleCloseFullScreen = () => {
    setFullScreenImage(null);
    if (showFullScreenGallery) {
      handleClose();
    }
  };

  // Completely rewrite the handleDialogClick function
  const handleDialogClick = (e) => {
    // Only stop propagation, don't prevent default
    e.stopPropagation();
  };

  // Add a new function to handle dialog content clicks
  const handleContentClick = (e) => {
    // Do nothing - this is just to capture clicks within the dialog content
    // without any side effects
  };

  if (shouldShowUnavailable) {
    return (
      <ThemeProvider theme={theme}>
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth="sm"
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: { xs: 0, md: '16px' },
              m: { xs: 0, md: '2vh' },
              overflow: 'hidden'
            },
            zIndex: 10300
          }}
        >
                  <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DirectionsCar sx={{ color: '#64748b', fontSize: 24 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1e293b',
                fontSize: '1.1rem',
                flex: 1
              }}
            >
              {t("vehicleDetails")}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#64748b',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              width: 32,
              height: 32,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                color: '#1e293b',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
          <DialogContent sx={{ p: { xs: 2, md: 4 }, minHeight: '300px' }}>
            <Alert 
              severity="info"
              variant="filled"
              sx={{
                width: '100%',
                height: '100%',
                minHeight: '200px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                background: theme.palette.v2.primary,
                borderRadius: '16px',
                p: 4
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.v2.textPrimary }}>
                {t("carUnavailableTitle")}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.v2.textPrimary }}>
                {t("carUnavailableDescription")}
              </Typography>
            </Alert>
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    );
  }

  // Desktop layout
  const DesktopLayout = () => (
    <Box sx={{ height: '100%', display: 'flex', position: 'relative', overflow: 'hidden' }}>
      {/* Left side - Image Gallery (40% width) */}
      <Box sx={{ 
        width: '50%', 
        height: '100%',
        backgroundColor: '#f1f5f5',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <ImageGallery
          images={images}
          onError={handleImageError}
          retailPrice={vehicleData?.retailPrice}
          salePrice={vehicleData?.salePrice}
          onFullScreen={setFullScreenImage}
          isDesktop={true}
        />
      </Box>

      {/* Right side - Details (60% width) */}
      <Box sx={{ 
        width: '50%', 
        height: '100%',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderLeft: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        {/* Summary header */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            {vehicleData?.badge && (
              <Chip label={(vehicleData.badge || '').toUpperCase()} size="small" sx={{ bgcolor: '#10b981', color: '#fff', fontWeight: 700 }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', flex: 1 }}>
              {vehicleData?.title || vehicleData?.modelGroup}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {vehicleData?.km > 0 && (
              <Chip label={`${formatPrice(vehicleData.km, numberLocale)} km`} size="small" />
            )}
            {vehicleData?.registrationYear && (
              <Chip label={`${vehicleData.registrationYear}`} size="small" />
            )}
            {vehicleData?.fuel && (
              <Chip label={vehicleData.fuel} size="small" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, justifyContent: 'space-between' }}>
            <Box>
              {isNewBadge && vehicleData?.retailPrice > vehicleData?.salePrice && (
                <Typography sx={{ textDecoration: 'line-through', color: '#9ca3af', fontWeight: 600 }}>
                  {formatPrice(vehicleData.retailPrice, numberLocale)} €
                </Typography>
              )}
              <Typography variant="h4" sx={{ fontWeight: 900, color: '#111827' }}>
                {formatPrice(vehicleData?.salePrice || 0, numberLocale)} €
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleBookCTA} disabled={chatLoading} sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700 }}>{t("bookVisit")}</Button>
              {vehicleData?.url && (
                <Button variant="outlined" onClick={() => window.open(vehicleData.url, '_blank')} sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}>{t("pageShort")}</Button>
              )}
              {vehicleData?.promotion_document__url && (
                <Button variant="outlined" color="secondary" onClick={() => window.open(vehicleData.promotion_document__url, '_blank')} sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700 }}>{t("promoShort")}</Button>
              )}
            </Box>
          </Box>
        </Box>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => {
            trackChatEvent.viewVehicleTab(vehicleData?.title, newValue === 0 ? 'info' : 'accessories');
            setTabIndex(newValue);
          }}
          centered
          sx={{
            borderBottom: '1px solid #e2e8f0',
            minHeight: '64px',
            bgcolor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          <StyledTab label={t("generalInfoTab")} />
          <StyledTab label={t("accessoriesTab")} />
        </Tabs>

        <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
          <TabPanel value={tabIndex} index={0}>
            <Box sx={{ 
              p: 3,
              backgroundColor: '#f8fafc',
              height: '100%',
              overflow: 'auto'
            }}>
              <GeneralInfo vehicleData={vehicleData} t={t} numberLocale={numberLocale} />
            </Box>
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <Box sx={{ 
              p: 3,
              backgroundColor: '#f8fafc',
              height: '100%',
              overflow: 'auto'
            }}>
              <Accessories
                standardAccessories={vehicleData?.standardAccessories}
                optionalAccessories={vehicleData?.optionalAccessories}
                isDesktop={true}
              />
            </Box>
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );

  // Mobile layout (existing layout)
  const MobileLayout = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? "0%" : "55%",
          opacity: isExpanded ? 0 : 1,
          display: isExpanded ? "none" : "flex"
        }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        style={{
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f1f5f9',
          position: 'relative'
        }}
      >
        <ImageGallery
          images={images}
          onError={handleImageError}
          retailPrice={vehicleData?.retailPrice}
          salePrice={vehicleData?.salePrice}
          onFullScreen={setFullScreenImage}
        />
      </motion.div>

      {/* Summary Section */}
      <Box sx={{ p: 2, pt: 1.5, borderTop: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {vehicleData?.badge && (
            <Chip label={(vehicleData.badge || '').toUpperCase()} size="small" sx={{ bgcolor: '#10b981', color: '#fff', fontWeight: 700 }} />
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', flex: 1 }}>
            {vehicleData?.title || vehicleData?.modelGroup}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {vehicleData?.km > 0 && (
            <Chip label={`${formatPrice(vehicleData.km, numberLocale)} km`} size="small" />
          )}
          {vehicleData?.registrationYear && (
            <Chip label={`${vehicleData.registrationYear}`} size="small" />
          )}
          {vehicleData?.fuel && (
            <Chip label={vehicleData.fuel} size="small" />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          {isNewBadge && vehicleData?.retailPrice > vehicleData?.salePrice && (
            <Typography sx={{ textDecoration: 'line-through', color: '#9ca3af', fontWeight: 600 }}>
              {formatPrice(vehicleData.retailPrice, numberLocale)} €
            </Typography>
          )}
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>
            {formatPrice(vehicleData?.salePrice || 0, numberLocale)} €
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleBookCTA}
            disabled={chatLoading}
            sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700 }}
          >
            {t("bookVisit")}
          </Button>
          {vehicleData?.url && (
            <Button
              variant="outlined"
              onClick={() => window.open(vehicleData.url, '_blank')}
              sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}
            >
              {t("pageShort")}
            </Button>
          )}
          {vehicleData?.promotion_document__url && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => window.open(vehicleData.promotion_document__url, '_blank')}
              sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700 }}
            >
              {t("promoShort")}
            </Button>
          )}
        </Box>
      </Box>

      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? "100%" : "45%",
          y: 0
        }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        style={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: 'rgba(37, 99, 235, 0.04)'
            }
          }}
        >
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isExpanded ? <ExpandMore /> : <ExpandLess />}
          </motion.div>
        </Box>

        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => {
            trackChatEvent.viewVehicleTab(vehicleData?.title, newValue === 0 ? 'info' : 'accessories');
            setTabIndex(newValue);
          }}
          centered
          sx={{
            borderBottom: '1px solid #e2e8f0',
            minHeight: '64px',
            bgcolor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          <StyledTab label={t("generalInfoTab")} />
          <StyledTab label={t("accessoriesTab")} />
        </Tabs>

        <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
          <TabPanel value={tabIndex} index={0}>
            <Box sx={{ 
              p: 3,
              backgroundColor: '#f8fafc',
              height: '100%',
              overflow: 'auto'
            }}>
              <GeneralInfo vehicleData={vehicleData} t={t} numberLocale={numberLocale} />
            </Box>
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <Box sx={{ 
              p: 3,
              backgroundColor: '#f8fafc',
              height: '100%',
              overflow: 'auto'
            }}>
              <Accessories
                standardAccessories={vehicleData?.standardAccessories}
                optionalAccessories={vehicleData?.optionalAccessories}
              />
            </Box>
          </TabPanel>
        </Box>
      </motion.div>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        ref={dialogRef}
        open={open}
        onClose={handleClose}
        onClick={handleDialogClick}
        fullScreen={window.innerWidth <= 768}
        container={document.getElementById('portal-root')}
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '100vw', md: 'min(90vw, 1400px)' },
            height: { xs: '100vh', md: 'min(90vh, 900px)' },
            minWidth: { md: '800px' },
            minHeight: { md: '600px' },
            maxWidth: { md: '1400px' },
            maxHeight: window.innerWidth <= 768 ? '100%' : '90vh',
            m: { xs: 0, md: '2vh' },
            overflow: 'hidden',
            borderRadius: { xs: 0, md: '24px' },
            bgcolor: '#ffffff',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }
        }}
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DirectionsCar sx={{ color: '#64748b', fontSize: 24 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1e293b',
                fontSize: '1.1rem',
                flex: 1
              }}
            >
              {t("vehicleDetails")}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#64748b',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              width: 32,
              height: 32,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                color: '#1e293b',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <DialogContent 
          sx={{ p: 0 }} 
          onClick={handleContentClick}
        >
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            isDesktop ? <DesktopLayout /> : <MobileLayout />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(fullScreenImage)}
        onClose={handleCloseFullScreen}
        onClick={handleDialogClick}
        fullScreen
        container={document.getElementById('portal-root')}
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'black',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)'
          }
        }}
      >
        <IconButton
          onClick={() => {
            const currentIndex = parseInt(fullScreenImage.match(/\/(\d+)\/large$/)?.[1] || 0);
            const newIndex = (currentIndex - 1 + 6) % 6;
            const baseUrl = fullScreenImage.replace(/\/\d+\/large$/, "");
            setFullScreenImage(`${baseUrl}/${newIndex}/large`);
          }}
          sx={{
            position: 'absolute',
            left: 16,
            color: 'white',
            zIndex: 1,
          }}
        >
          <ArrowBackIos />
        </IconButton>

        <IconButton
          onClick={() => {
            const currentIndex = parseInt(fullScreenImage.match(/\/(\d+)\/large$/)?.[1] || 0);
            const newIndex = (currentIndex + 1) % 6;
            const baseUrl = fullScreenImage.replace(/\/\d+\/large$/, "");
            setFullScreenImage(`${baseUrl}/${newIndex}/large`);
          }}
          sx={{
            position: 'absolute',
            right: 16,
            color: 'white',
            zIndex: 1,
          }}
        >
          <ArrowForwardIos />
        </IconButton>

        <IconButton
          onClick={handleCloseFullScreen}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'white',
            zIndex: 1,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              transform: 'rotate(90deg)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {fullScreenImage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '32px'
            }}
          >
            <CardMedia
              component="img"
              image={fullScreenImage}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '16px',
                boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
              }}
            />
          </motion.div>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

export default VehicleDialog;
